'use strict';

define('vb/binding/expression',[
  'knockout',
  'vb/private/log',
  'vb/private/utils',
  'vb/private/constants',
  'ojs/ojbindingprovider',
], (ko, Log, Utils, Constants, BindingProvider) => {
  const logger = Log.getLogger('/vb/binding/expression');

  // Check for {{..}} and [[..]] at the beginning of strings to avoid matching
  // any usages mid string
  const ATTR_EXP = /^(?:\{\{)([^]+)(?:\}\})$/;
  const ATTR_EXP_RO = /^(?:\[\[)([^]+)(?:\]\])$/;
  const WHITESPACE = /\s/g; // matches whitespace
  const CONSTANTS_EXP = /(((\$global|\$application|\$page|\$flow)\.constants\.)|(\$constants\.))[\\$A-Z_a-z]/g; // eslint-disable-line max-len
  const VARIABLES_EXP = /(((\$global|\$application|\$page|\$flow)\.variables\.)|(\$variables\.))[\\$A-Z_a-z]/g; // eslint-disable-line max-len
  // allows [] following variable names.
  const VARIABLES_EXP2 = /((((\$global|\$application|\$page|\$flow)\.variables)|(\$variables))\[(.*?)\])/g;

  /**
   * Wrap toProxyObj in a proxy to prevent updates if it is a POJO and doesn't have the oj-vb-no-object-proxy symbol
   * property which is set on JET POJOs only.
   *
   * @param {Object} toProxyObj - the object to proxy
   * @param {Object} listener - proxy listener to prevent updates
   * @returns {Proxy|object}
   */
  const createProxyIfNecessary = (toProxyObj, listener) => {
    if (Utils.isObjectOrArray(toProxyObj) && Utils.isPrototypeOfObject(toProxyObj)
      && !toProxyObj[Constants.SKIP_EXPRESSION_PROXY_SYMBOL]) {
      return new Proxy(toProxyObj, listener);
    }
    return toProxyObj;
  };

  /**
   * Wraps the structure and throws exceptions if any of the objects are mutated. Note that we do not
   * use Object.freeze since we do not want to alter the original structures or prevent writes to them
   * outside of our usage.
   *
   * We also do not want to proactively clone the structures since (a) this is expensive and (b) does not
   * help the user fix their code.
   *
   * The motivation for doing this is to prevent random errors. If a mutation occurs within an expression
   * than that mutation will occur whenever something in the expression changes (causing that expression
   * to reevaluate). That reevaluation can occur seemingly randomly, causing the mutation to also occur
   * randomly - which is very bad!
   *
   * Dev note: this will only protect on modern browsers. That should be good enough for things to fail, and
   * we can hope that not all testing occurs on older browsers!
   *
   * @param {Object} obj - The object to write protect
   * @param {String} expression - The expression that was used
   * @returns {Proxy|object}
   * @private
   */
  const preventWritesToStructure = (obj, expression) => {
    const listener = {
      get(target, property) {
        const realValue = target[property];

        const config = Object.getOwnPropertyDescriptor(target, property);
        if (config && config.configurable === false && config.writable === false) {
          return realValue;
        }

        return createProxyIfNecessary(realValue, listener);
      },
      set() {
        throw new Error(`The expression '${expression}' has a method that has mutated a variable. This can `
          + 'lead to unexpected behavior when this expression is evaluated. Consider using '
          + 'the \'callModuleFunctionAction\' instead for a safe way of mutating variables.');
      },
    };

    return createProxyIfNecessary(obj, listener);
  };

  /**
   * Return the expression string without {{ }} or [[ ]] or null if the string doesn't have {{ }} or [[ ]].
   * @param  {String} v - an expression with {{ }} or [[]]
   * @return {String|null|undefined} the expression without the bracket
   */
  const getExpression = (v) => {
    let exp;
    if (v) {
      const trimmedVal = v.trim();
      exp = ATTR_EXP.exec(trimmedVal);
      exp = exp ? exp[1] : null;
      if (!exp) {
        exp = ATTR_EXP_RO.exec(trimmedVal);
        exp = exp ? exp[1] : null;
      }
    }

    return exp;
  };

  /**
   * Returns true if regex token appears in expression
   * @param {String} expr - expression
   * @param {RegExp} token - Regexp of the token(s) to look for in expression
   * @return {*|boolean|String|RegExpMatchArray}
   */
  const hasTokenInExpr = (expr, token) => {
    const strippedExpr = typeof expr === 'string' && getExpression(expr);
    // strips white spaces before checking expression
    return strippedExpr && strippedExpr.replace(WHITESPACE, '').match(token);
  };

  /**
   * A reusable evaluated value that can be used to capture complex expressions.
   * @category Binding
   */
  class Expression {
    /**
     * Matches whitespace
     * @type {RegExp}
     */
    static get WHITESPACE_REGEX() {
      return WHITESPACE;
    }

    /**
     * Creates an expression from a string. This string is expected to be just the expression, i.e.
     *  $variables.foo + $variables.bar
     * and not more complicated code, although we could support this in the future.
     *
     * Since the function generally runs in some context, this can be passed via the
     * injectedContextMap, which each key/value pair being injected into the function before
     * evaluation.
     *
     * @param {String} expression
     * @param {Object} injectedContextMap A map of context objects that should be put in scope
     * @param {Boolean} [prohibitWrites=true] If true, will throw an exception if a variable in context is written to
     * @param {Function} [proxyFunc] callback if provided will be used over the default. Rules provides a
     * custom proxy function
     * @returns {Function}
     */
    static createFromString(expression, injectedContextMap, prohibitWrites, proxyFunc) {
      // JET's createBindingExpressionEvaluator() expects this structure for evaluating expressions
      const $data = {};
      const injectedContext = { $data };
      if (injectedContextMap) {
        const shouldProhibitWrites = prohibitWrites !== false;
        const operation = shouldProhibitWrites
          ? proxyFunc || preventWritesToStructure : (value) => value;
        Object.entries(injectedContextMap).forEach(([key, value]) => {
          // Expression evaluation copies injected contexts onto a synthetic `$data` object. `$withEnv` uses its
          // receiver to know which VB context to wrap, so bind it to the source context before exposing it there.
          const contextValue = key === '$withEnv' && typeof value === 'function'
            ? value.bind(injectedContextMap) : value;
          $data[key] = operation(contextValue, expression, key);
        });
      }

      try {
        const evaluate = BindingProvider.createBindingExpressionEvaluator(expression, injectedContext)
          .bind(null, injectedContext);
        const wrapperFn = () => {
          try {
            return evaluate();
          } catch (e) {
            const exprsnStr = typeof expression === 'string' ? expression.trim() : expression;
            const error = new Error(`Could not evaluate expression ${exprsnStr}: ${e.message || e}`);
            error.cause = e;
            throw error;
          }
        };
        return Expression.create(wrapperFn);
      } catch (e) {
        logger.error('Could not create expression', expression, e);

        // The return value is expected to be a function. This makes it so that the code doesn't throw again
        return Expression.create(() => undefined);
      }
    }

    /**
     * Creates a new expression that will evaluate the passed in function whenever it
     * itself is evaluated.
     *
     * An expression can be directly assigned to a variable. When a variable assigned to an
     * expression is read, the evaluated result is read.
     *
     * An expression can also be assigned to a component property and will automatically
     * updated when the expression value has changed.
     *
     * An expression is called with some context as follows:
     *
     * variables: The innermost scope's set of variables
     * application: The application object
     * container: The container object, i.e. the page object or a flow object (may be undefined)
     * action: The action object (may be undefined).
     *
     * @param {Function} expressionFunction - A function to evaluate when the expression is evaluated
     * @returns {Function} an expression (do not treat this as an observable!)
     */
    static create(expressionFunction) {
      return ko.pureComputed(expressionFunction.bind(null));
    }

    /**
     * Return true is v starts with '{{' (or '[[') and ends with '}}' (or ']]').
     * @param {String} v
     * @returns {boolean}
     */
    static isExpression(v) {
      return !!(typeof v === 'string' && getExpression(v));
    }

    /**
     * Return the expression string without {{ }} or [[ ]] or null if the string doesn't have {{ }} or [[ ]].
     * @param  {String} v - an expression with {{ }} or [[]]
     * @return {String|null|undefined} the expression without the bracket
     */
    static getExpression(v) {
      return getExpression(v);
    }

    /**
     * looks for expressions in the object/scalar, and evaluates them (invokes the function immediately).
     * This is in contrast to StateUtils.getValueOrExpression, that leaves them as variables.
     *
     * @param {*} value the possible expression. if its a string, and does not have the expression syntax, return as-is.
     * @param {Object} scopes map of dollar-variables available to the expressions.
     *
     * TODO: the name 'scopes' is used elsewhere, but we should come up with a better name.
     * @returns {*}
     */
    static getEvaluated(value, scopes) {
      if (!value) {
        return value;
      }

      if (typeof value === 'string') {
        const expression = getExpression(value);
        if (expression) {
          const fnc = Expression.createFromString(expression, scopes);
          return fnc();
        }
      } else if (Array.isArray(value)) {
        return value.map((item) => Expression.getEvaluated(item, scopes));
      } else if (!Utils.isPrototypeOfObject(value)) {
        return value;
      } else if (Utils.isObject(value)) {
        const obj = {};
        Object.keys(value).forEach((key) => {
          obj[key] = Expression.getEvaluated(value[key], scopes);
        });
        return obj;
      }

      // if it's not an expression, just return the value as is
      return value;
    }

    /**
     * wraps in a try/catch, just in case.
     * returns the original expression if the expression definition is falsey,
     * or null if there is an exception.

     * @param {*} expression
     * @param {Object} [context] optional properties that can be referenced in the expression
     * @returns {*}
     */
    static getEvaluatedSafe(expression, context = {}) {
      try {
        return expression ? Expression.getEvaluated(expression, context) : expression;
      } catch (e) {
        logger.error('unable to create expression', expression, e);
      }
      return null;
    }

    /**
     * Returns true if we have an expression and it has a 'constants' in it. See the regex above
     * for this.
     * This does not check if the expression is a pure constants expression, iow that it only
     * contains 'constants' and no other - like variables, builtins etc.
     * @param {String} expr
     * @returns {boolean}
     */
    static hasConstantsInExpr(expr) {
      return hasTokenInExpr(expr, CONSTANTS_EXP);
    }

    /**
     * Returns true if we have an expression and it has a 'variables' in it. See the regex above
     * for this.
     * This does not check if the expression is a pure variables expression, iow that it only
     * contains 'variables' and no other - like constants, builtins etc.
     * @param {String} expr
     * @returns {boolean}
     */
    static hasVariablesInExpr(expr) {
      return hasTokenInExpr(expr, VARIABLES_EXP) || hasTokenInExpr(expr, VARIABLES_EXP2);
    }
  }

  return Expression;
});

